import { useState, useEffect, useCallback } from 'react';
import { useServices, useToast } from '@/app/providers';
import type { AccountStatement, AccountStatementParams, ExportFormat } from '@yawi/types';
import { downloadBlob, generateReportFilename } from '../utils/download';

interface UseAccountStatementOptions {
  autoLoad?: boolean;
  initialParams?: AccountStatementParams;
}

export const useAccountStatement = (options: UseAccountStatementOptions = {}) => {
  const { autoLoad = true, initialParams = {} } = options;
  const { reportRepo } = useServices();
  const toast = useToast();

  const [statement, setStatement] = useState<AccountStatement | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [params, setParams] = useState<AccountStatementParams>(initialParams);

  const fetchStatement = useCallback(async () => {
    setLoading(true);
    try {
      const data = await reportRepo.getMyAccountStatement(params);
      setStatement(data);
    } catch (e) {
      console.error('Error loading account statement:', e);
      toast.error('Error al cargar el estado de cuenta');
    } finally {
      setLoading(false);
    }
  }, [reportRepo, params, toast]);

  const exportStatement = useCallback(async (format: ExportFormat) => {
    setExporting(true);
    try {
      const blob = await reportRepo.exportMyAccountStatement(format, params);
      const filename = generateReportFilename('estado-cuenta', format, params.unitId);
      downloadBlob(blob, filename);
      toast.success('Reporte exportado correctamente');
    } catch (e) {
      console.error('Error exporting account statement:', e);
      toast.error('Error al exportar el estado de cuenta');
    } finally {
      setExporting(false);
    }
  }, [reportRepo, params, toast]);

  useEffect(() => {
    if (autoLoad) {
      fetchStatement();
    }
  }, [autoLoad, fetchStatement]);

  return {
    statement,
    loading,
    exporting,
    params,
    setParams,
    refetch: fetchStatement,
    exportStatement,
  };
};
